import React from "react";
import styled from "styled-components";
import Image from "next/image";
import Head from "next/head";
import * as Router from "next/router";
import { CourseJsonLd } from "next-seo";

import firebase from "../../../firebase.client";
import * as UserContext from "../../../components/UserContext";
import * as config from "../../../config.client";
import * as u from "../../../utils";
import * as T from "../../../components/Typography";
import LessonCard from "../../../components/AcademyLessonCard";
import BackButton from "../../../components/BackButton";

export default function Collection(props) {
  const router = Router.useRouter();
  const userInfo = React.useContext(UserContext.Context);
  const [savedCourseIds, setSavedCourseIds] = React.useState([]);
  React.useEffect(() => {
    getMyCourses();
  }, [userInfo]);
  const collection = props.collection;
  const courses = props.courses;

  return (
    <S.Container>
      {getHead()}
      <CourseJsonLd {...getJsonLdProps()} />
      <S.NavigateBack>
        <BackButton text="Go to Academy" backTo="/academy" />
      </S.NavigateBack>
      <S.CollectionHeader>
        <S.CollectionImage>
          <Image
            src={collection.courseCardImage.url}
            alt={collection.courseCardImage.title}
            layout="fill"
            objectFit="cover"
          />
        </S.CollectionImage>
        <S.HeaderTextContainer>
          <T.H1>{collection.courseName}</T.H1>
          <T.CXL weight={"bold"}>
            {collection.coursesCollection.total} courses
          </T.CXL>
          <T.CXL>{collection.courseDescription}</T.CXL>
        </S.HeaderTextContainer>
      </S.CollectionHeader>
      <S.Hr />
      <S.Lessons>{courses?.map(getCourse)}</S.Lessons>
    </S.Container>
  );

  async function getMyCourses() {
    if (!userInfo.id) return;
    const ref = firebase.firestore().collection("users").doc(userInfo.id);
    const data = (await ref.get()).data();
    if (data?.savedCourses) setSavedCourseIds(data.savedCourses);
  }

  function getCourse(course) {
    return (
      <LessonCard
        key={course.lessonName}
        isFromCollection
        isMember
        savedCourseIds={savedCourseIds}
        setSavedCourseIds={setSavedCourseIds}
        userInfo={userInfo}
        {...course}
      />
    );
  }

  function getHead() {
    const title =
      u.trimStr(collection.courseName, 30) + " | Academy | AllBright";
    const description = collection.courseDescription || "";

    const info = {
      title,
      description,
      imgW: 365,
      imgH: 365,
      imgUrl: collection.courseCardImage.url,
      imgAlt: collection.courseCardImage.title,
      url: `${config.ALLBRIGHT_URL}${router.asPath}`,
    };

    const metaTags = [
      ...u.generateMetaTags(info),
      ...u.generateMetaType("course"),
    ];

    return (
      <Head>
        <title>{title}</title>
        {metaTags.map((elm) => (
          <meta {...elm} key={elm.name || elm.itemprop || elm.property} />
        ))}
      </Head>
    );
  }

  function getJsonLdProps() {
    return {
      courseName: collection.courseName,
      providerName: "AllBright Academy",
      providerUrl: `${config.ALLBRIGHT_URL}${router.asPath}`,
      description: collection.courseDescription || "",
    };
  }
}

export async function getStaticPaths() {
  return { paths: [], fallback: "blocking" };
}

export async function getStaticProps({ params }) {
  const { CONTENTFUL_GENERAL } = require("../../../config");

  try {
    const axios = require("axios");
    const collectionData = await axios.post(
      CONTENTFUL_GENERAL,
      getCollectionQuery(params.collection)
    );
    const [collection] = collectionData.data.data.courseCollection.items;

    const courseNames = collection.coursesCollection.items
      .filter((a) => a)
      .map((c) => c.lessonName)
      .join('", "');
    const coursesData = await axios.post(
      CONTENTFUL_GENERAL,
      getCoursesQuery(courseNames)
    );
    const courses = coursesData.data.data.lessonCollection.items;
    return { props: { collection, courses }, revalidate: 3600 };
  } catch (err) {
    console.log(params);
    console.error("✘ academy collection error: ", err);
    return { notFound: true };
  }

  function getCollectionQuery(collection) {
    return {
      query: `{
        courseCollection(where: { urlSlug: "${collection}" }) {
          items {
            urlSlug
            courseName
            courseDescription
            courseCardImage { url title }
            coursesCollection {
              total
              items { lessonName }
            }
          }
        }
      }`,
    };
  }

  function getCoursesQuery(courseNames) {
    return {
      query: `{
        lessonCollection(where: { lessonName_in: ["${courseNames}"] }) {
          items {
            locale
            lessonName
            courseTags
            categoryTags
            urlSlug
            lessonIntroductionText
            featuredCourseMobileImage { url title}
            featuredCourseImage { url title}
            featuredCourseDescription
            coverImage { title url }
            sys { id }
            lessonActivitiesCollection { total }
            lessonAuthor
          }
        }
      }`,
    };
  }
}

const S = {};

S.Container = styled.div`
  max-width: 1440px;
  margin: auto;
  padding: 20px 16px;

  @media ${u.device.tablet} {
    padding: 20px;
  }

  @media ${u.device.desktop} {
    padding: 40px;
  }
`;

S.NavigateBack = styled.div`
  margin: 40px 0 20px;
`;

S.CollectionHeader = styled.header`
  display: grid;
  grid-template-rows: auto auto;
  grid-gap: 40px;
  min-height: 30vw;
  @media (min-width: 900px) {
    grid-template-columns: auto auto;
    grid-template-rows: auto;
  }
`;

S.CollectionImage = styled.div`
  position: relative;
  height: 365px;
  width: 365px;
  margin: 0 auto;
  max-width: 75vw;
  @media (min-width: 900px) {
    max-width: 365px;
    width: 30vw;
  }
`;

S.HeaderTextContainer = styled.div`
  align-self: center;
`;

S.Hr = styled.hr`
  border: 1px solid ${u.colours.shellOverlay};
  margin: 30px auto;
  max-width: 1440px;
  width: 80%;
`;

S.H3Desktop = styled.h3`
  margin: 0 20px 20px;
  @media ${u.device.desktop} {
    margin: 0 0 30px 0;
  }
`;

S.H3Mobile = styled.h3`
  display: block;
  margin-bottom: 20px;
  text-align: center;
  @media ${u.device.tablet} {
    margin-bottom: 30px;
  }
  @media ${u.device.desktop} {
    display: none;
  }
`;

S.Lessons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 25px;
  @media ${u.device.tablet} {
    justify-content: space-between;
  }
`;
